import { useEffect, useState } from 'react';
import { get } from 'lodash';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { PersonAdd, Visibility, VisibilityOff } from '@mui/icons-material';
import Page from '../../components/Page';
import { registerAdmin } from '../../axiosLib/services/admin';
import { getAllInstitutions } from '../../axiosLib/services/institution/admin';
import { getAvailableRoles } from '../../axiosLib/services/adminRoles/admin';

const INITIAL_FORM = {
  name: '',
  username: '',
  phone: '',
  email: '',
  password: '',
  nickName: '',
  institutionId: '',
  roleSet: [],
};

export default function AdminRegistration() {
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [institutions, setInstitutions] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    getAllInstitutions((err, result) => {
      if (!err) {
        setInstitutions(get(result, 'institutions', result) || []);
      }
    });
    getAvailableRoles((err, result) => {
      if (!err) {
        setAvailableRoles(get(result, 'roles', result) || []);
      }
    });
  }, []);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleRoleToggle = (role) => {
    const currentRoles = form.roleSet || [];
    if (currentRoles.includes(role)) {
      handleChange('roleSet', currentRoles.filter((r) => r !== role));
    } else {
      handleChange('roleSet', [...currentRoles, role]);
    }
  };

  const handleSubmit = () => {
    if (!form.username || !form.password || !form.email) {
      setSnackbar({ open: true, message: 'Username, email, and password are required', severity: 'error' });
      return;
    }
    if (!form.institutionId) {
      setSnackbar({ open: true, message: 'Please select an institution', severity: 'error' });
      return;
    }

    setSaving(true);
    registerAdmin(form, (err, result) => {
      setSaving(false);
      if (!err) {
        setSnackbar({ open: true, message: 'Admin registered successfully!', severity: 'success' });
        setForm({ ...INITIAL_FORM });
      } else {
        const errMsg = get(err, 'response.data.username') ||
          get(err, 'response.data.error') ||
          get(err, 'message', 'Registration failed');
        setSnackbar({ open: true, message: errMsg, severity: 'error' });
      }
    });
  };

  return (
    <Page title="Register Admin">
      <Container maxWidth="md">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" gutterBottom>
            Register New Admin
          </Typography>
        </Stack>

        <Card sx={{ p: 4 }}>
          <Stack spacing={3}>
            {/* Name & Username */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Full Name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                fullWidth
              />
              <TextField
                label="Username *"
                value={form.username}
                onChange={(e) => handleChange('username', e.target.value)}
                fullWidth
              />
            </Stack>

            {/* Nick Name & Phone */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Nick Name"
                value={form.nickName}
                onChange={(e) => handleChange('nickName', e.target.value)}
                fullWidth
              />
              <TextField
                label="Phone"
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                fullWidth
              />
            </Stack>

            {/* Email & Password */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Email *"
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                fullWidth
              />
              <TextField
                label="Password *"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {/* Institution */}
            <FormControl fullWidth>
              <InputLabel>Institution *</InputLabel>
              <Select
                value={form.institutionId}
                onChange={(e) => handleChange('institutionId', e.target.value)}
                label="Institution *"
              >
                <MenuItem value="">
                  <em>-- Select Institution --</em>
                </MenuItem>
                {institutions.map((inst) => (
                  <MenuItem key={inst._id} value={inst._id}>
                    {inst.institutionName || inst._id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Roles */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Admin Roles
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {availableRoles.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    color={form.roleSet.includes(role) ? 'primary' : 'default'}
                    variant={form.roleSet.includes(role) ? 'filled' : 'outlined'}
                    onClick={() => handleRoleToggle(role)}
                    size="small"
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Submit */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <PersonAdd />}
                onClick={handleSubmit}
                disabled={saving}
                size="large"
                sx={{
                  background: 'linear-gradient(45deg, #00d4ff, #7b2ff7)',
                  fontWeight: 'bold',
                  px: 5,
                  py: 1.5,
                  '&:hover': { background: 'linear-gradient(45deg, #00b8d4, #6a1bd4)' },
                }}
              >
                {saving ? 'Registering...' : 'Register Admin'}
              </Button>
            </Box>
          </Stack>
        </Card>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Page>
  );
}
